import type { OfflineDatabaseSqlStatement } from './offlineDatabase.statements';
import { booleanToSqlValue, jsonToSqlValue } from './offlineDatabase.serialization';

type TimestampFactory = () => string;

const isoNow = (): string => new Date().toISOString();

export const buildSharedItemLinkCacheStatement = (
  link: any,
  getTimestamp: TimestampFactory = isoNow
): OfflineDatabaseSqlStatement => ({
  sql: `INSERT OR REPLACE INTO shared_item_links
              (id, source_item_id, target_item_id, include_room, include_floor, include_photos, version, created_at, updated_at)
              VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
  params: [
    link.id,
    link.sourceItemId,
    link.targetItemId,
    booleanToSqlValue(Boolean(link.includeRoom)),
    link.includeFloor !== undefined ? booleanToSqlValue(link.includeFloor) : 1,
    booleanToSqlValue(Boolean(link.includePhotos)),
    link.version || 1,
    link.createdAt || getTimestamp(),
    link.updatedAt || getTimestamp(),
  ],
});

export const buildSharedQuestionMappingCacheStatements = (
  link: any,
  getTimestamp: TimestampFactory = isoNow
): OfflineDatabaseSqlStatement[] => {
  const mappings = link.mappings || [];

  return mappings.map((mapping: any) => ({
    sql: `INSERT OR REPLACE INTO shared_question_mappings
                  (id, shared_item_link_id, source_question_id, target_question_id, shared_key, validation_rules, version, created_at, updated_at)
                  VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    params: [
      mapping.id,
      link.id,
      mapping.sourceQuestionId,
      mapping.targetQuestionId,
      mapping.sharedKey,
      jsonToSqlValue(mapping.validationRules),
      mapping.version || 1,
      mapping.createdAt || getTimestamp(),
      mapping.updatedAt || getTimestamp(),
    ],
  }));
};

export const buildSharedLinksForItemSelectStatement = (
  itemId: number
): OfflineDatabaseSqlStatement => ({
  sql: `SELECT * FROM shared_item_links
           WHERE source_item_id = ? OR target_item_id = ?`,
  params: [itemId, itemId],
});

export const buildSharedLinksForItemsSelectStatement = (
  itemIds: number[]
): OfflineDatabaseSqlStatement => {
  if (itemIds.length === 0) {
    return {
      sql: 'SELECT * FROM shared_item_links WHERE 1 = 0',
      params: [],
    };
  }

  const placeholders = itemIds.map(() => '?').join(',');

  return {
    sql: `SELECT * FROM shared_item_links
               WHERE source_item_id IN (${placeholders})
                  OR target_item_id IN (${placeholders})`,
    params: [...itemIds, ...itemIds],
  };
};

export const buildQuestionMappingsForLinkSelectStatement = (
  linkId: number
): OfflineDatabaseSqlStatement => ({
  sql: `SELECT * FROM shared_question_mappings
          WHERE shared_item_link_id = ?`,
  params: [linkId],
});

export const buildCompletedResponsesForItemSelectStatement = (
  propertyId: number,
  itemId: number
): OfflineDatabaseSqlStatement => ({
  sql: `SELECT r.* FROM review_responses r
           INNER JOIN reviews rev ON r.review_id = rev.id
           WHERE rev.property_id = ?
           AND r.item_id = ?
           AND rev.status = 'completed'
           ORDER BY rev.submitted_at DESC, rev.updated_at DESC
          LIMIT 1`,
  params: [propertyId, itemId],
});
